import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'

const AuthContext = createContext(null)

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'
const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

// Inactivity timeout — enforced both live (IdleTimeoutGuard, while the tab is
// open) and here at load time, so closing the browser and reopening it after
// being idle longer than this also forces a fresh login instead of silently
// refreshing off the (much longer-lived) Cognito refresh token.
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour
const _LAST_ACTIVITY_KEY = 'sd_last_activity'

const DEV_USER = {
  sub: 'dev-user-001',
  email: 'dev@structuradata.local',
  name: 'Dev User',
  tenantId: import.meta.env.VITE_DEV_TENANT_ID || 'dev-tenant-001',
  groups: ['platform-admin'],
}

function decodeJwt(token) {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

function isExpired(payload) {
  return !payload || payload.exp * 1000 < Date.now() + 30_000
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined)
  const [selectedTenantId, setSelectedTenantId] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const adminTenant = params.get('admin_tenant')
    if (adminTenant) {
      localStorage.setItem('sd_selected_tenant', adminTenant)
      const url = new URL(window.location.href)
      url.searchParams.delete('admin_tenant')
      window.history.replaceState({}, '', url.toString())
      return adminTenant
    }
    return localStorage.getItem('sd_selected_tenant') || null
  })

  const recordActivity = useCallback(() => {
    localStorage.setItem(_LAST_ACTIVITY_KEY, String(Date.now()))
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('sd_access_token')
    localStorage.removeItem('sd_id_token')
    localStorage.removeItem('sd_refresh_token')
    localStorage.removeItem('sd_selected_tenant')
    localStorage.removeItem(_LAST_ACTIVITY_KEY)
    setUser(null)
    setSelectedTenantId(null)
    window.location.href = '/login'
  }, [])

  const switchTenant = useCallback((tenantId) => {
    setSelectedTenantId(tenantId || null)
    if (tenantId) {
      localStorage.setItem('sd_selected_tenant', tenantId)
    } else {
      localStorage.removeItem('sd_selected_tenant')
    }
  }, [])

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('sd_refresh_token')
    if (!refreshToken) return null
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      if (!res.ok) return null
      const data = await res.json()
      localStorage.setItem('sd_access_token', data.accessToken)
      localStorage.setItem('sd_id_token', data.idToken)
      return data.accessToken
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    async function init() {
      if (DEV_BYPASS_AUTH) {
        setUser(DEV_USER)
        return
      }

      // A stale activity timestamp means the tab/browser was closed (or just
      // left alone) longer than the idle timeout — don't silently refresh off
      // the 30-day Cognito refresh token in that case, force a fresh login.
      const lastActivity = Number(localStorage.getItem(_LAST_ACTIVITY_KEY))
      if (lastActivity && Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
        logout()
        return
      }

      let accessToken = localStorage.getItem('sd_access_token')
      let accessPayload = decodeJwt(accessToken)

      if (isExpired(accessPayload)) {
        accessToken = await refresh()
        accessPayload = decodeJwt(accessToken)
      }

      if (!accessToken || isExpired(accessPayload)) {
        setUser(null)
        return
      }

      const idPayload = decodeJwt(localStorage.getItem('sd_id_token'))
      recordActivity()
      setUser({
        sub:      accessPayload.sub,
        email:    idPayload?.email ?? accessPayload.username,
        name:     idPayload?.given_name ?? idPayload?.name ?? idPayload?.email ?? accessPayload.username,
        tenantId: idPayload?.['custom:tenant_id'] ?? accessPayload['custom:tenant_id'] ?? accessPayload.sub,
        role:     idPayload?.['custom:role'] ?? accessPayload['custom:role'] ?? 'Analyst',
        groups:   accessPayload['cognito:groups'] ?? [],
      })
    }
    init()
  }, [refresh, logout, recordActivity])

  const getAccessToken = useCallback(async () => {
    let token = localStorage.getItem('sd_access_token')
    const payload = decodeJwt(token)
    if (isExpired(payload)) {
      token = await refresh()
    }
    return token
  }, [refresh])

  const getIdToken = useCallback(async () => {
    const accessPayload = decodeJwt(localStorage.getItem('sd_access_token'))
    if (isExpired(accessPayload)) {
      await refresh()
    }
    return localStorage.getItem('sd_id_token')
  }, [refresh])

  const apiFetch = useCallback(async (path, options = {}) => {
    const token = await getIdToken()
    return fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(selectedTenantId ? { 'X-Tenant-ID': selectedTenantId } : {}),
        ...(options.headers ?? {}),
      },
    })
  }, [getIdToken, selectedTenantId])

  // The tenant whose data is currently being viewed: the platform-admin's
  // switcher selection if active, otherwise the user's own tenant. This is
  // the single source of truth for "tenant_id" in any request — read it
  // here rather than recomputing selectedTenantId || user?.tenantId per page.
  const effectiveTenantId = useMemo(
    () => selectedTenantId || user?.tenantId,
    [selectedTenantId, user]
  )

  return (
    <AuthContext.Provider value={{ user, logout, getAccessToken, getIdToken, apiFetch, selectedTenantId, switchTenant, effectiveTenantId, recordActivity }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
